import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserLevel } from '@reenstudio/app/core/enum/user-level';
import { CerAddUserApiRequest } from '@reenstudio/app/core/models/api/cer/cer-api-model';
import { UserAddEditApiRequest } from '@reenstudio/app/core/models/api/user/user-api-model';
import { CerService } from '@reenstudio/app/core/services/api/cer.service';
import { UserApiService } from '@reenstudio/app/core/services/api/user-service';
import { MaterialModule } from '@reenstudio/app/material.module';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { switchMap } from 'rxjs';

@Component({
  selector: 'app-cer-add-edit',
  templateUrl: './cer-add-edit.component.html',
  styleUrls: ['./cer-add-edit.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgFor,
    NgIf,
    MaterialModule,
    NgxSkeletonLoaderModule,
  ],
})
export class CerAddEditComponent implements OnInit {
  @Input() type: string;

  form: FormGroup;
  loading: boolean = false;
  cerId: number;
  userId: string;

  constructor(
    private cerService: CerService,
    private userService: UserApiService,
    private snackBarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: NGXLogger
  ) {}

  ngOnInit(): void {
    this.forminit();
  }

  forminit() {
    this.form = new FormGroup({
      name: new FormControl(null, Validators.required),
      lat: new FormControl(),
      lon: new FormControl(),
      email: new FormControl(
        null,
        this.type === 'add' ? [Validators.required, Validators.email] : null
      ),
    });

    if (this.type === 'edit') {
      this.loading = true;
      this.route.params.subscribe((params) => {
        const recordId = params['id'];
        if (recordId) {
          this.cerService.getCer(recordId).subscribe({
            next: (data) => {
              this.logger.debug('Cers edit received cer data', data);

              let latLon = {
                latitude: '',
                longitude: '',
              };

              if (data.position_json) {
                latLon = <{ latitude: string; longitude: string }>(
                  JSON.parse(data.position_json)
                );
              }

              this.form.patchValue({
                name: data.description,
                lat: latLon.latitude,
                lon: latLon.longitude,
              });
              this.cerId = data.id;
              this.loading = false;
            },
          });
        }
      });
    }
  }

  onSubmit() {
    this.loading = true;

    const cerData: { description: string; position_json: string } = {
      description: this.form.value.name,
      position_json: JSON.stringify({
        latitude: this.form.value.lat,
        longitude: this.form.value.lon,
      }),
    };

    const userData: UserAddEditApiRequest = {
      name: this.form.value.name,
      email: this.form.value.email,
      level: UserLevel.CER,
    };

    if (this.type === 'add') {
      this.cerService
        .addCer(cerData)
        .pipe(
          switchMap((cerResponse) => {
            this.logger.debug('Cer Response', cerResponse);
            this.cerId = cerResponse.details.cer.id;
            return this.userService.addUser(userData);
          }),
          switchMap((userResponse) => {
            this.logger.debug('User Response', userResponse);
            this.userId = userResponse.details.user.id;
            const params: CerAddUserApiRequest = {
              cer: this.cerId,
            };
            return this.cerService.addCerUser(this.userId, params);
          })
        )
        .subscribe({
          next: () => {
            this.snackBarService.success('CER aggiunta con successo');
            this.loading = false;
            this.router.navigate(['/administration/cer']);
          },
          error: (err) => {
            console.error('Errore durante le chiamate:', err);
          },
        });
    } else if (this.type === 'edit') {
      this.cerService.editCer(this.cerId, cerData).subscribe({
        next: () => {
          this.snackBarService.success('CER modificata con successo');
          this.loading = false;
          this.router.navigate(['/administration/cer']);
        },
      });
    }
  }
}
